import {useEffect, useId, useRef, useState, type KeyboardEvent} from 'react';
import {CalendarDays, ChevronDown, ChevronLeft, ChevronRight, Clock3} from 'lucide-react';
import {Select} from '../../app/Select';
import {localDate} from '../../lib/time';
import './schedule-date-time.css';

type Props = {value: string; onChange: (value: string) => void; disabled?: boolean; timeZone: string};
const weekdays = ['Mandag', 'Tirsdag', 'Onsdag', 'Torsdag', 'Fredag', 'Lørdag', 'Søndag'];
const pad = (value: number) => String(value).padStart(2, '0');
const dateFromKey = (value: string) => new Date(`${value}T12:00:00Z`);
const dateKey = (date: Date) => date.toISOString().slice(0, 10);
const formatDate = (value: string, options: Intl.DateTimeFormatOptions) => dateFromKey(value).toLocaleDateString('da-DK', {...options, timeZone: 'UTC'});
function addDays(value: string, amount: number) {
  const date = dateFromKey(value);
  date.setUTCDate(date.getUTCDate() + amount);
  return dateKey(date);
}
function addMonths(value: string, amount: number) {
  const date = dateFromKey(value), day = date.getUTCDate();
  date.setUTCDate(1);
  date.setUTCMonth(date.getUTCMonth() + amount);
  const end = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + 1, 0)).getUTCDate();
  date.setUTCDate(Math.min(day, end));
  return dateKey(date);
}

/** The calendar selects wall time only; ScheduleForm resolves time zones and DST. */
export function ScheduleDateTime({value, onChange, disabled = false, timeZone}: Props) {
  const id = useId(), root = useRef<HTMLDivElement>(null), trigger = useRef<HTMLButtonElement>(null);
  const focusRequested = useRef(false);
  const today = localDate(new Date().toISOString(), timeZone);
  const day = value.slice(0, 10), [pendingTime, setPendingTime] = useState('09:00');
  const time = value.slice(11, 16) || pendingTime;
  const [open, setOpen] = useState(false), [focusedDay, setFocusedDay] = useState(day || today), [month, setMonth] = useState((day || today).slice(0, 7));
  const first = `${month}-01`, weekOffset = (dateFromKey(first).getUTCDay() + 6) % 7;
  const dates = Array.from({length: 42}, (_, index) => addDays(first, index - weekOffset));
  const close = (restoreFocus = false) => {setOpen(false); if (restoreFocus) trigger.current?.focus();};
  const show = () => {
    if (disabled) return;
    const next = day || today;
    setFocusedDay(next); setMonth(next.slice(0, 7)); focusRequested.current = true; setOpen(true);
  };
  const choose = (next: string) => {if (!disabled) {onChange(`${next}T${time}`); close(true);}};
  const changeTime = (next: string) => {setPendingTime(next); if (day) onChange(`${day}T${next}`);};
  const moveFocus = (next: string) => {focusRequested.current = true; setFocusedDay(next); setMonth(next.slice(0, 7));};
  const moveMonth = (amount: number) => {
    const next = addMonths(`${month}-${focusedDay.slice(8, 10)}`, amount);
    setMonth(next.slice(0, 7)); setFocusedDay(next);
  };
  const navigate = (event: KeyboardEvent<HTMLButtonElement>, current: string) => {
    const offset = (dateFromKey(current).getUTCDay() + 6) % 7;
    const next = event.key === 'ArrowLeft' ? addDays(current, -1)
      : event.key === 'ArrowRight' ? addDays(current, 1)
      : event.key === 'ArrowUp' ? addDays(current, -7)
      : event.key === 'ArrowDown' ? addDays(current, 7)
      : event.key === 'Home' ? addDays(current, -offset)
      : event.key === 'End' ? addDays(current, 6 - offset)
      : event.key === 'PageUp' ? addMonths(current, event.shiftKey ? -12 : -1)
      : event.key === 'PageDown' ? addMonths(current, event.shiftKey ? 12 : 1) : null;
    if (next) {event.preventDefault(); moveFocus(next);}
  };
  useEffect(() => {
    if (!open) return;
    if (focusRequested.current) {
      root.current?.querySelector<HTMLButtonElement>(`[data-date="${focusedDay}"]`)?.focus();
      focusRequested.current = false;
    }
  }, [open, month, focusedDay]);
  useEffect(() => {
    if (disabled) setOpen(false);
  }, [disabled]);
  useEffect(() => {
    if (!open) return;
    const outside = (event: PointerEvent) => {if (!root.current?.contains(event.target as Node)) setOpen(false);};
    // Close this calendar before a containing publication dialog handles Escape.
    const escape = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape') {event.preventDefault(); event.stopPropagation(); setOpen(false); trigger.current?.focus();}
    };
    document.addEventListener('pointerdown', outside);
    window.addEventListener('keydown', escape, true);
    return () => {document.removeEventListener('pointerdown', outside); window.removeEventListener('keydown', escape, true);};
  }, [open]);

  return <div ref={root} className="cap4publish-schedule-date-time" onBlur={event => {
    if (event.relatedTarget && !event.currentTarget.contains(event.relatedTarget)) setOpen(false);
  }}>
    <div className="cap4publish-schedule-fields">
      <div className="cap4publish-schedule-date-field">
        <span id={`${id}-date-label`} className="cap4publish-schedule-field-label">Dato</span>
        <button ref={trigger} type="button" className="cap4publish-schedule-date-trigger" disabled={disabled}
          aria-label={`Dato: ${day ? formatDate(day, {day: 'numeric', month: 'long', year: 'numeric'}) : 'Vælg dato'}`}
          aria-haspopup="dialog" aria-expanded={open} aria-controls={open ? `${id}-calendar` : undefined}
          onClick={() => open ? close() : show()} onKeyDown={event => {if (event.key === 'ArrowDown') {event.preventDefault(); show();}}}>
          <CalendarDays size={19} aria-hidden="true"/>
          <span>{day ? formatDate(day, {weekday: 'short', day: 'numeric', month: 'short', year: 'numeric'}) : 'Vælg dato'}</span>
          <ChevronDown size={16} aria-hidden="true"/>
        </button>
      </div>
      <div className="cap4publish-schedule-time-field">
        <span className="cap4publish-schedule-field-label">Klokkeslæt</span>
        <div className="cap4publish-schedule-time-controls">
          <Clock3 size={18} aria-hidden="true"/>
          <Select aria-label="Time" disabled={disabled} value={time.slice(0, 2)} onChange={event => changeTime(`${event.target.value}:${time.slice(3, 5)}`)}>
            {Array.from({length: 24}, (_, hour) => <option value={pad(hour)} key={hour}>{pad(hour)}</option>)}
          </Select>
          <span aria-hidden="true">:</span>
          <Select aria-label="Minut" disabled={disabled} value={time.slice(3, 5)} onChange={event => changeTime(`${time.slice(0, 2)}:${event.target.value}`)}>
            {Array.from({length: 60}, (_, minute) => <option value={pad(minute)} key={minute}>{pad(minute)}</option>)}
          </Select>
        </div>
      </div>
    </div>
    {open && <div id={`${id}-calendar`} className="cap4publish-schedule-calendar" role="dialog" aria-label="Vælg dato">
      <div className="cap4publish-schedule-month-nav">
        <button type="button" aria-label="Forrige måned" onClick={() => moveMonth(-1)}><ChevronLeft size={18} aria-hidden="true"/></button>
        <span id={`${id}-month`} aria-live="polite">{formatDate(first, {month: 'long', year: 'numeric'})}</span>
        <button type="button" aria-label="Næste måned" onClick={() => moveMonth(1)}><ChevronRight size={18} aria-hidden="true"/></button>
      </div>
      <div role="grid" aria-labelledby={`${id}-month`} className="cap4publish-schedule-grid">
        <div role="row" className="cap4publish-schedule-weekdays">
          {weekdays.map(name => <span role="columnheader" aria-label={name} key={name}>{name.slice(0, 2)}</span>)}
        </div>
        {Array.from({length: 6}, (_, week) => <div role="row" key={week}>
          {dates.slice(week * 7, week * 7 + 7).map(date => <div role="gridcell" aria-selected={day === date} key={date}>
            <button type="button" data-date={date} tabIndex={focusedDay === date ? 0 : -1}
              className={`${date.startsWith(month) ? '' : 'is-outside'} ${date === day ? 'is-selected' : ''} ${date === today ? 'is-today' : ''}`}
              aria-label={formatDate(date, {weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'})}
              aria-current={date === today ? 'date' : undefined}
              onFocus={() => setFocusedDay(date)} onKeyDown={event => navigate(event, date)} onClick={() => choose(date)}>{Number(date.slice(8))}</button>
          </div>)}
        </div>)}
      </div>
      <div className="cap4publish-schedule-calendar-footer">
        <button type="button" onClick={() => choose(today)}>I dag</button>
        <button type="button" onClick={() => close(true)}>Luk kalender</button>
      </div>
    </div>}
  </div>;
}
